import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// English translations
const translationEN = {
  "app": {
    "title": "Muscle Guide - Your AI Fitness Assistant",
    "meta": {
      "description": "A personalized AI fitness assistant for workout plans, diet plans, and fitness tracking."
    }
  },
  "register": {
    "title": "Create Your Account",
    "subtitle": "Join Muscle Guide to track your fitness journey",
    "step1": "Account Information",
    "step2": "Personal Information",
    "step3": "Fitness Information",
    "alreadyHaveAccount": "Already have an account?",
    "createAccount": "Create Account",
    "submitting": "Creating account..."
  },
  "login": {
    "title": "Login"
  },
  "auth": {
    "login": "Login",
    "register": "Register",
    "logout": "Logout",
    "email": "Email",
    "password": "Password",
    "confirmPassword": "Confirm Password",
    "forgotPassword": "Forgot Password?",
    "dontHaveAccount": "Don't have an account?",
    "alreadyHaveAccount": "Already have an account?",
    "signUp": "Sign Up",
    "signIn": "Sign In",
    "errorMessage": "Invalid email or password",
    "successRegister": "Registration successful! You can now log in.",
    "passwordWeak": "Weak",
    "passwordMedium": "Medium",
    "passwordStrong": "Strong",
    "resetPasswordTitle": "Reset your password",
    "forgotPasswordDesc": "Enter the email address associated with your account and we'll send you a link to reset your password.",
    "emailPlaceholder": "your@email.com",
    "sendResetLink": "Send Reset Link",
    "backToLogin": "Back to Login",
    "resetEmailSent": "Check your email",
    "resetEmailSentDesc": "We sent a password reset link to your email. The link expires in 1 hour.",
    "resetError": "Something went wrong. Please try again.",
    "resetPassword": "Reset Password",
    "setNewPassword": "Set a new password for your account",
    "newPassword": "New Password",
    "passwordMinLength": "Password must be at least 8 characters",
    "passwordMismatch": "Passwords do not match",
    "passwordResetSuccess": "Password reset successfully!",
    "passwordResetSuccessDesc": "You can now sign in with your new password.",
    "invalidResetLink": "Invalid Reset Link",
    "invalidResetLinkDesc": "This password reset link is invalid or has expired.",
    "requestNewLink": "Request a new link"
  },
  "dashboard": {
    "welcome": "Welcome",
    "subtitle": "What would you like to do today?",
    "workoutPlans": "Workout Plans",
    "workoutPlansDesc": "Generate and manage your AI-powered workout routines",
    "dietPlans": "Diet Plans",
    "dietPlansDesc": "Create personalized meal plans and track daily nutrition",
    "progress": "Progress Tracking",
    "progressDesc": "Log body measurements, workouts, and see evolution charts",
    "aiAssistant": "AI Assistant",
    "aiAssistantDesc": "Chat with AI to customize plans and log meals",
    "profile": "Profile & Settings",
    "profileDesc": "Update your personal info, goals, and preferences"
  },
  "navigation": {
    "profile": "Profile",
    "workoutPlans": "Workout Plans",
    "dietPlans": "Diet Plans",
    "progress": "Progress",
    "logout": "Logout",
    "home": "Home",
    "settings": "Settings",
    "chatWithAI": "Chat with AI"
  },
  "registration": {
    "step1": "Account Information",
    "step2": "Personal Information",
    "step3": "Fitness Information",
    "next": "Next",
    "previous": "Previous",
    "submit": "Create Account",
    "completeFitnessProfile": "Complete your fitness profile to get personalized workout and diet plans",
    "selectFitnessObjective": "Select your primary fitness objective",
    "importantForDiet": "Important for Diet Plan",
    "affectsCalorieRequirements": "This affects your calorie requirements and diet recommendations",
    "howLongExercise": "How long can you exercise in a single session?",
    "howManyDays": "How many days can you commit to training each week?",
    "whereWorkout": "Where will you be working out?",
    "bodyweight": "Bodyweight Exercises",
    "affectsWorkoutPlans": "Affects Workout Plans",
    "currentExperience": "Your current fitness/training experience",
    "day": "day",
    "days": "days",
    "year": "year of training",
    "years": "years of training"
  },
  "nav": {
    "userData": "User Data",
    "workoutPlans": "Workout Plans",
    "dietPlans": "Diet Plans",
    "aiAssistant": "AI Assistant",
    "progress": "Progress"
  },
  "userData": {
    "title": "Your Profile",
    "personalInfo": "Personal Information",
    "name": "Name",
    "age": "Age",
    "gender": "Gender",
    "weight": "Weight",
    "height": "Height",
    "fitnessGoals": "Fitness Goals",
    "workoutSettings": "Workout Settings",
    "timePerSession": "Time Per Session",
    "daysPerWeek": "Days Per Week",
    "activityLevel": "Activity Level",
    "workoutLocation": "Workout Location",
    "experienceLevel": "Experience Level",
    "save": "Save Changes",
    "saved": "Changes saved successfully!",
    "confirmMessage": "Please confirm your information",
    "minutesAbbr": "min",
    "cm": "cm",
    "kg": "kg",
    "loadSuccess": "User data loaded successfully!",
    "saveSuccess": "Information saved successfully!",
    "saveError": "Failed to update profile. Please try again."
  },
  "workoutPlans": {
    "title": "Workout Plans",
    "createNew": "Create New Plan",
    "beginner": "Beginner",
    "intermediate": "Intermediate",
    "advanced": "Advanced",
    "modifyPlan": "Need to modify your workout plan?",
    "chatAIAssistant": "Chat with the AI Assistant to customize your workout plan or get guidance on specific exercises.",
    "goAIAssistant": "Go to AI Assistant",
    "generateNew": "Generate New Plan",
    "exerciseInfoTip": "Click on any exercise name to see how to perform it on YouTube.",
    "yourWorkoutPlan": "Your Workout Plan",
    "askAboutPlan": "Let me create a personalized workout plan for you based on your goals.",
    "details": "Details",
    "goal": "Goal",
    "level": "Level",
    "duration": "Duration",
    "weeks": "weeks",
    "location": "Location",
    "overview": "Overview",
    "schedule": "Schedule",
    "additionalNotes": "Additional Notes",
    "progressionPlan": "Progression Plan",
    "videoGuide": "Video Guide",
    "findVideo": "Find Video Guide",
    "generate": "Generate Workout Plan",
    "loading": "Loading plans...",
    "noPlans": "No workout plans found. Generate one to get started!",
    "missingUserData": "To generate a personalized workout plan, I need some information about you:",
    "fillUserData": "Fill User Data",
    "savedPlans": "Saved Plans",
    "planTitle": "Plan {{number}}",
    "createPlan": "Create a Workout Plan",
    "manageUserData": "Enter Your Information",
    "generating": "Generating your workout plan...",
    "saveToDevice": "Save to device",
    "saveToAccount": "Save to account",
    "generateButton": "Generate Workout Plan",
    "generated": "Generated Workout Plan",
    "previous": "Previous",
    "save": "Save",
    "next": "Next",
    "exercise": "Exercise",
    "sets": "Sets",
    "reps": "Reps",
    "rest": "Rest",
    "loadMore": "Load More"
  },
  "dietPlans": {
    "title": "Diet Plans",
    "createNew": "Create New Plan",
    "generating": "Generating your personalized diet plan...",
    "dailyCalories": "Daily Calories",
    "macros": "Macronutrients",
    "protein": "Protein",
    "carbs": "Carbohydrates",
    "fat": "Fat",
    "breakfast": "Breakfast",
    "lunch": "Lunch",
    "dinner": "Dinner",
    "snack": "Snack",
    "ingredients": "Ingredients",
    "instructions": "Instructions",
    "modify": "Modify Plan",
    "delete": "Delete Plan",
    "fillDataFirst": "Please fill in your personal information first!",
    "readyToCreate": "Ready to create your personalized diet plan?",
    "generate": "Generate Diet Plan",
    "generateNew": "Generate New Plan",
    "caloriesPerDay": "calories per day",
    "cal": "cal",
    "dailyTotals": "Daily Totals",
    "calories": "Calories",
    "modifyPlanTitle": "Need to modify your diet plan?",
    "modifyPlanDescription": "Chat with the AI Assistant to customize your diet plan or get nutritional guidance.",
    "goToAI": "Go to AI Assistant",
    "errors": {
      "userDataMissing": "Please fill in your personal information first!",
      "incompleteData": "Please fill in all required personal information before generating a plan.",
      "generateFailed": "Failed to generate diet plan. Please try again later.",
      "invalidApiKey": "Invalid API key. Please check your OpenAI API key.",
      "unavailableModel": "The requested AI model is currently unavailable. Please try again later.",
      "rateLimitExceeded": "Rate limit exceeded. Please try again later.",
      "networkError": "Network error. Please check your internet connection and try again.",
      "invalidUserData": "Invalid user data. Please check your personal information and ensure weight, height and age are valid numbers.",
      "invalidPlanStructure": "Received an invalid diet plan structure. Please try generating the plan again."
    }
  },
  "aiAssistant": {
    "title": "AI Assistant",
    "description": "Chat with your AI assistant to modify your workout plans, diet plans, track your food intake, and get personalized fitness advice.",
    "placeholder": "Ask about your workout or diet plans...",
    "send": "Send",
    "loading": "Considering your fitness plans...",
    "plansLoaded": "Plans loaded successfully! I'll consider them in my responses.",
    "workoutLoaded": "Workout plan loaded! I'll reference it in my responses.",
    "dietLoaded": "Diet plan loaded! I'll reference it in my responses.",
    "bothLoaded": "Both workout and diet plans loaded! I'll consider them in my responses.",
    "clearChat": "Clear Chat",
    "confirmClearHistory": "Are you sure you want to clear your chat history?",
    "chatCleared": "Chat history cleared. How can I help you today?",
    "completeProfile": "Please complete your profile information before using the AI assistant.",
    "createPlansFirst": "Generate workout and diet plans first to get personalized advice.",
    "plansLoadedMessage": "I've loaded your {{workoutPlan}} {{conjunction}} {{dietPlan}} and will consider them in my responses.",
    "foodLogged": "Food '{{foodName}}' logged with {{calories}} calories.",
    "workoutPlanChanged": "Workout plan has been updated successfully!",
    "dietPlanChanged": "Diet plan has been updated successfully!",
    "welcomeMessage": "Hi there! I'm your AI fitness assistant. I can help with workout advice, diet tips, or tracking your food intake. How can I assist you today?",
    "errors": {
      "generic": "Sorry, I encountered an error. Please try again.",
      "apiKey": "API key error. Please contact support.",
      "model": "The AI model is currently unavailable. Please try again later.",
      "rateLimit": "You've reached the maximum number of requests. Please try again in a few minutes.",
      "network": "Network error. Please check your internet connection and try again.",
      "completeProfile": "Please complete your profile before using the AI assistant."
    }
  },
  "progress": {
    "title": "Progress Tracking",
    "addEntry": "Add Entry",
    "date": "Date",
    "weight": "Weight",
    "exercise": "Exercise",
    "sets": "Sets",
    "reps": "Reps",
    "rest": "Rest",
    "foodDiary": "Food Diary",
    "addFood": "Log Food",
    "mealType": "Meal Type",
    "calories": "Calories",
    "save": "Save",
    "cancel": "Cancel",
    "noEntries": "No entries yet. Start tracking your progress!",
    "successMessage": "Entry added successfully!",
    "tabs": {
      "workout": "Workout Logs",
      "diet": "Diet Logs",
      "body": "Body Measurements"
    },
    "logYourWorkout": "Log Your Workout",
    "logYourDiet": "Log Your Diet",
    "foodNamePlaceholder": "e.g. Chicken Breast",
    "servingSizePlaceholder": "e.g. 100g",
    "createWorkoutPlanFirst": "Please create a workout plan first!",
    "goToWorkoutPlans": "Go to Workout Plans",
    "fillDataFirst": "Please fill in your personal information first!",
    "editingExistingLog": "Editing existing log",
    "protein": "Protein (g)",
    "carbs": "Carbs (g)",
    "fat": "Fat (g)",
    "servingSize": "Serving Size",
    "bodyMeasurements": {
      "chest": "Chest",
      "waist": "Waist",
      "arms": "Arms",
      "bodyFat": "Body Fat",
      "chestLabel": "Chest (cm, optional)",
      "waistLabel": "Waist (cm, optional)",
      "armsLabel": "Arms (cm, optional)",
      "bodyFatLabel": "Body Fat (%, optional)",
      "hipsLabel": "Hips (cm, optional)",
      "thighsLabel": "Thighs (cm, optional)",
      "chestPlaceholder": "Enter chest measurement",
      "waistPlaceholder": "Enter waist measurement",
      "armsPlaceholder": "Enter arms measurement",
      "hipPlaceholder": "Enter hips measurement",
      "thighsPlaceholder": "Enter thighs measurement",
      "bodyFatPlaceholder": "Enter body fat percentage",
      "hips": "Hips",
      "thighs": "Thighs"
    },
    "notes": "Notes",
    "notesPlaceholder": "Add any notes about your measurements",
    "set": "Set",
    "weight_kg": "Weight (kg)",
    "workoutHistory": "Workout History",
    "history": "History",
    "update": "Update",
    "saveWorkout": "Save Workout",
    "updateWorkout": "Update Workout",
    "saveMeasurement": "Save Measurement",
    "updateMeasurement": "Update Measurement",
    "foodItems": "Food Items",
    "actions": "Actions",
    "remove": "Remove",
    "totals": "Totals",
    "dietLogHistory": "Diet Log History",
    "noFoodsLogged": "No foods logged",
    "macroSummary": "P: {{protein}}g / C: {{carbs}}g / F: {{fat}}g",
    "protein_abbr": "P",
    "carbs_abbr": "C",
    "fat_abbr": "F",
    "food": "Food",
    "meal": "Meal",
    "workout": "Workout",
    "snacks": "Snacks",
    "dietLogSave": "Save Diet Log",
    "dietLogUpdate": "Update Diet Log",
    "noFoodEntries": "No diet entries yet. Start logging your food intake above!",
    "exercises": "exercises",
    "metrics": {
      "weight": "Weight",
      "bodyFat": "Body Fat",
      "waist": "Waist",
      "chest": "Chest",
      "arms": "Arms",
      "hips": "Hips",
      "thighs": "Thighs"
    },
    "charts": {
      "bodyEvolution": "Body Measurements Over Time",
      "caloriesTrend": "Daily Calories",
      "macrosTrend": "Macros Over Time",
      "volumeEvolution": "Workout Volume Over Time",
      "setsTrend": "Sets & Exercises Per Workout",
      "totalVolume": "Total Volume (kg)",
      "totalSets": "Total Sets",
      "exercisesCount": "Exercises",
      "calories": "Calories (kcal)",
      "protein": "Protein",
      "carbs": "Carbs",
      "fat": "Fat",
      "logTodayFood": "Log Today's Food",
      "logTodayFoodDesc": "Track your meals with macro breakdowns in the Diet tab.",
      "goToDiet": "Go to Diet",
      "logWorkout": "Log a Workout",
      "logWorkoutDesc": "Track sets, reps, and weight with the Hevy-style workout logger.",
      "logWorkoutBtn": "Log Workout"
    }
  },
  "fitnessGoals": {
    "build_muscle": "Build Muscle",
    "lose_weight": "Lose Weight",
    "improve_fitness": "Improve Fitness",
    "increase_strength": "Increase Strength",
    "maintain_health": "Maintain Health",
    "muscle_gain": "Build Muscle",
    "weight_loss": "Lose Weight",
    "strength": "Increase Strength",
    "endurance": "Improve Endurance",
    "general_fitness": "General Fitness"
  },
  "activityLevels": {
    "sedentary": "Sedentary (little or no exercise)",
    "lightly_active": "Lightly Active (light exercise 1-3 days/week)",
    "moderately_active": "Moderately Active (moderate exercise 3-5 days/week)",
    "very_active": "Very Active (hard exercise 6-7 days/week)",
    "extremely_active": "Extremely Active (very hard exercise & physical job)"
  },
  "mealTypes": {
    "breakfast": "Breakfast",
    "lunch": "Lunch",
    "dinner": "Dinner",
    "snack": "Snack"
  },
  "common": {
    "loading": "Loading...",
    "errorOccurred": "An error occurred",
    "tryAgain": "Please try again",
    "noData": "No data available",
    "save": "Save",
    "cancel": "Cancel",
    "delete": "Delete",
    "edit": "Edit",
    "add": "Add",
    "remove": "Remove",
    "confirm": "Confirm",
    "back": "Back",
    "next": "Next",
    "done": "Done",
    "success": "Success!",
    "error": "Error!",
    "yes": "Yes",
    "no": "No",
    "male": "Male",
    "female": "Female",
    "other": "Other",
    "settings": "Settings",
    "language": "Language",
    "step": "Step",
    "of": "of",
    "complete": "Complete",
    "greeting": "Hello",
    "goToUserData": "Go to User Data",
    "workoutPlan": "Workout Plan",
    "dietPlan": "Diet Plan",
    "and": "and",
    "successfully": "successfully",
    "updated": "updated",
    "renderError": "There was an error displaying the plan. Please reset and try again.",
    "resetPlan": "Reset Plan",
    "errorLoadingData": "Error loading data. Please refresh the page.",
    "untitled": "Untitled Plan",
    "noDescription": "No description available",
    "day": "Day",
    "meal": "Meal",
    "appName": "Muscle Guide",
    "synced": "Synced with server",
    "savedLocally": "Saved locally",
    "syncing": "Syncing...",
    "syncError": "Sync error",
    "sync": "Sync to server",
    "submitting": "Submitting..."
  },
  "languageSelection": {
    "language": "Language",
    "en": "English",
    "pt-BR": "Brazilian Portuguese",
    "es": "Spanish"
  },
  "notifications": {
    "success": "Success!",
    "error": "Error!",
    "warning": "Warning!",
    "info": "Information"
  },
  "errors": {
    "nameRequired": "Name is required",
    "invalidEmail": "Please enter a valid email address",
    "passwordLength": "Password must be at least 8 characters",
    "passwordWeak": "Password is too weak. Add more complexity",
    "passwordsNoMatch": "Passwords do not match",
    "ageRequired": "Age is required",
    "ageRange": "Age must be between 16 and 100",
    "weightRequired": "Weight is required",
    "weightRange": "Weight must be between 30 and 300 kg",
    "heightRequired": "Height is required",
    "heightRange": "Height must be between 100 and 250 cm"
  },
  "workoutDays": {
    "upperBody": "Upper Body",
    "lowerBody": "Lower Body",
    "fullBody": "Full Body",
    "push": "Push Day",
    "pull": "Pull Day",
    "legs": "Legs Day",
    "chest": "Chest Day",
    "back": "Back Day",
    "shoulders": "Shoulders Day",
    "arms": "Arms Day",
    "core": "Core Day",
    "cardio": "Cardio Day",
    "rest": "Rest Day",
    "day1": "Day 1",
    "day2": "Day 2",
    "day3": "Day 3",
    "day4": "Day 4",
    "day5": "Day 5",
    "day6": "Day 6",
    "day7": "Day 7"
  },
  "bodyParts": {
    "chest": "Chest",
    "back": "Back",
    "shoulders": "Shoulders",
    "biceps": "Biceps",
    "triceps": "Triceps",
    "legs": "Legs",
    "quads": "Quadriceps",
    "hamstrings": "Hamstrings",
    "calves": "Calves",
    "glutes": "Glutes",
    "abs": "Abs",
    "core": "Core",
    "fullBody": "Full Body",
    "upperBody": "Upper Body",
    "lowerBody": "Lower Body"
  },
  "workoutLocations": {
    "home": "Home",
    "gym": "Gym",
    "outdoor": "Outdoor"
  },
  "experienceLevels": {
    "beginner": "Beginner",
    "intermediate": "Intermediate",
    "advanced": "Advanced"
  },
  "subscription": {
    "headline": "Unlock personalized AI-powered workout & diet plans",
    "monthlyPlan": "Monthly Plan",
    "month": "month",
    "subscribeBtn": "Subscribe — $10/month",
    "redirecting": "Redirecting to payment...",
    "checkoutError": "Failed to create checkout session. Please try again.",
    "portalError": "Failed to open subscription portal.",
    "active": "Subscription Active!",
    "plan": "Plan",
    "goToDashboard": "Go to Dashboard",
    "manage": "Manage Subscription",
    "activating": "Activating your subscription...",
    "cancelled": "Payment was cancelled. You can try again when ready.",
    "features": {
      "aiWorkoutPlans": "AI Workout Plans",
      "aiDietPlans": "AI Diet Plans",
      "aiChatAssistant": "AI Chat Assistant",
      "progressTracking": "Progress Tracking",
      "evolutionCharts": "Evolution Charts & Analytics"
    }
  }
};

// Brazilian Portuguese translations
const translationPTBR = {
  "app": {
    "title": "Muscle Guide - Seu Assistente de Fitness com IA",
    "meta": {
      "description": "Um assistente de fitness com IA personalizado para planos de treino, planos alimentares e acompanhamento de fitness."
    }
  },
  "register": {
    "title": "Crie Sua Conta",
    "subtitle": "Junte-se ao Guia Muscular para acompanhar sua jornada fitness",
    "step1": "Informações da Conta",
    "step2": "Informações Pessoais",
    "step3": "Informações de Fitness",
    "alreadyHaveAccount": "Já tem uma conta?",
    "createAccount": "Criar Conta",
    "submitting": "Criando conta..."
  },
  "login": {
    "title": "Entrar"
  },
  "auth": {
    "login": "Entrar",
    "register": "Cadastrar",
    "logout": "Sair",
    "email": "E-mail",
    "password": "Senha",
    "confirmPassword": "Confirmar Senha",
    "forgotPassword": "Esqueceu a senha?",
    "dontHaveAccount": "Não tem uma conta?",
    "alreadyHaveAccount": "Já tem uma conta?",
    "signUp": "Cadastrar",
    "signIn": "Entrar",
    "errorMessage": "E-mail ou senha inválidos",
    "successRegister": "Cadastro realizado com sucesso! Você já pode fazer login.",
    "passwordWeak": "Fraca",
    "passwordMedium": "Média",
    "passwordStrong": "Forte",
    "resetPasswordTitle": "Redefinir sua senha",
    "forgotPasswordDesc": "Digite o e-mail associado à sua conta e enviaremos um link para redefinir sua senha.",
    "emailPlaceholder": "seu@email.com",
    "sendResetLink": "Enviar Link de Redefinição",
    "backToLogin": "Voltar ao Login",
    "resetEmailSent": "Verifique seu e-mail",
    "resetEmailSentDesc": "Enviamos um link de redefinição de senha para seu e-mail. O link expira em 1 hora.",
    "resetError": "Algo deu errado. Tente novamente.",
    "resetPassword": "Redefinir Senha",
    "setNewPassword": "Defina uma nova senha para sua conta",
    "newPassword": "Nova Senha",
    "passwordMinLength": "A senha deve ter pelo menos 8 caracteres",
    "passwordMismatch": "As senhas não coincidem",
    "passwordResetSuccess": "Senha redefinida com sucesso!",
    "passwordResetSuccessDesc": "Agora você pode entrar com sua nova senha.",
    "invalidResetLink": "Link Inválido",
    "invalidResetLinkDesc": "Este link de redefinição de senha é inválido ou expirou.",
    "requestNewLink": "Solicitar novo link"
  },
  "dashboard": {
    "welcome": "Bem-vindo",
    "subtitle": "O que você gostaria de fazer hoje?",
    "workoutPlans": "Planos de Treino",
    "workoutPlansDesc": "Gere e gerencie suas rotinas de treino com IA",
    "dietPlans": "Planos Alimentares",
    "dietPlansDesc": "Crie planos alimentares personalizados e acompanhe a nutrição diária",
    "progress": "Acompanhamento de Progresso",
    "progressDesc": "Registre medidas corporais, treinos e veja gráficos de evolução",
    "aiAssistant": "Assistente IA",
    "aiAssistantDesc": "Converse com a IA para personalizar planos e registrar refeições",
    "profile": "Perfil e Configurações",
    "profileDesc": "Atualize suas informações pessoais, metas e preferências"
  },
  "navigation": {
    "profile": "Perfil",
    "workoutPlans": "Planos de Treino",
    "dietPlans": "Dieta",
    "progress": "Progresso",
    "logout": "Sair",
    "home": "Início",
    "settings": "Configurações",
    "chatWithAI": "Conversar com IA"
  },
  "registration": {
    "step1": "Informações da Conta",
    "step2": "Informações Pessoais",
    "step3": "Informações de Fitness",
    "next": "Próximo",
    "previous": "Anterior",
    "submit": "Criar Conta",
    "completeFitnessProfile": "Complete seu perfil de fitness para obter planos de treino e dieta personalizados",
    "selectFitnessObjective": "Selecione seu objetivo principal de fitness",
    "importantForDiet": "Importante para o Plano Alimentar",
    "affectsCalorieRequirements": "Isso afeta seus requisitos calóricos e recomendações dietéticas",
    "howLongExercise": "Por quanto tempo você pode se exercitar em uma única sessão?",
    "howManyDays": "Quantos dias você pode se comprometer a treinar por semana?",
    "whereWorkout": "Onde você vai treinar?",
    "bodyweight": "Exercícios com Peso Corporal",
    "affectsWorkoutPlans": "Afeta Planos de Treino",
    "currentExperience": "Sua experiência atual de fitness/treinamento",
    "day": "dia",
    "days": "dias",
    "year": "ano de treinamento",
    "years": "anos de treinamento"
  },
  "nav": {
    "userData": "Dados do Usuário",
    "workoutPlans": "Planos de Treino",
    "dietPlans": "Planos Alimentares",
    "aiAssistant": "Assistente IA",
    "progress": "Progresso"
  },
  "userData": {
    "title": "Seu Perfil",
    "personalInfo": "Informações Pessoais",
    "name": "Nome",
    "age": "Idade",
    "gender": "Gênero",
    "weight": "Peso",
    "height": "Altura",
    "fitnessGoals": "Objetivos de Fitness",
    "workoutSettings": "Configurações de Treino",
    "timePerSession": "Tempo por Sessão",
    "daysPerWeek": "Dias por Semana",
    "activityLevel": "Nível de Atividade",
    "workoutLocation": "Local de Treino",
    "experienceLevel": "Nível de Experiência",
    "save": "Salvar Alterações",
    "saved": "Alterações salvas com sucesso!",
    "confirmMessage": "Por favor, confirme suas informações",
    "minutesAbbr": "min",
    "cm": "cm",
    "kg": "kg",
    "loadSuccess": "Dados do usuário carregados com sucesso!",
    "saveSuccess": "Informações salvas com sucesso!",
    "saveError": "Falha ao atualizar perfil. Por favor, tente novamente."
  },
  "workoutPlans": {
    "title": "Planos de Treino",
    "createNew": "Criar Novo Plano",
    "beginner": "Iniciante",
    "intermediate": "Intermediário",
    "advanced": "Avançado",
    "modifyPlan": "Precisa modificar seu plano de treino?",
    "chatAIAssistant": "Converse com o Assistente IA para personalizar seu plano de treino ou obter orientação sobre exercícios específicos.",
    "goAIAssistant": "Ir para o Assistente IA",
    "generateNew": "Gerar Novo Plano",
    "exerciseInfoTip": "Clique em qualquer nome de exercício para ver como executá-lo no YouTube.",
    "yourWorkoutPlan": "Seu Plano de Treino",
    "askAboutPlan": "Deixe-me criar um plano de treino personalizado para você baseado nos seus objetivos.",
    "details": "Detalhes",
    "goal": "Objetivo",
    "level": "Nível",
    "duration": "Duração",
    "weeks": "semanas",
    "location": "Local",
    "overview": "Visão Geral",
    "schedule": "Cronograma",
    "additionalNotes": "Notas Adicionais",
    "progressionPlan": "Plano de Progressão",
    "videoGuide": "Guia em Vídeo",
    "findVideo": "Encontrar Guia em Vídeo",
    "generate": "Gerar Plano de Treino",
    "loading": "Carregando planos...",
    "noPlans": "Nenhum plano de treino encontrado. Gere um para começar!",
    "missingUserData": "Para gerar um plano de treino personalizado, preciso de algumas informações sobre você:",
    "fillUserData": "Preencher Dados",
    "savedPlans": "Planos Salvos",
    "planTitle": "Plano {{number}}",
    "createPlan": "Criar um Plano de Treino",
    "manageUserData": "Inserir Suas Informações",
    "generating": "Gerando seu plano de treino...",
    "saveToDevice": "Salvar no dispositivo",
    "saveToAccount": "Salvar na conta",
    "generateButton": "Gerar Plano de Treino",
    "generated": "Plano de Treino Gerado",
    "previous": "Anterior",
    "save": "Salvar",
    "next": "Próximo",
    "exercise": "Exercício",
    "sets": "Séries",
    "reps": "Repetições",
    "rest": "Descanso",
    "loadMore": "Carregar Mais"
  },
  "dietPlans": {
    "title": "Planos Alimentares",
    "createNew": "Criar Novo Plano",
    "generating": "Gerando seu plano alimentar personalizado...",
    "dailyCalories": "Calorias Diárias",
    "macros": "Macronutrientes",
    "protein": "Proteína",
    "carbs": "Carboidratos",
    "fat": "Gordura",
    "breakfast": "Café da Manhã",
    "lunch": "Almoço",
    "dinner": "Jantar",
    "snack": "Lanche",
    "ingredients": "Ingredientes",
    "instructions": "Instruções",
    "modify": "Modificar Plano",
    "delete": "Excluir Plano",
    "fillDataFirst": "Por favor, preencha suas informações pessoais primeiro!",
    "readyToCreate": "Pronto para criar seu plano alimentar personalizado?",
    "generate": "Gerar Plano Alimentar",
    "generateNew": "Gerar Novo Plano",
    "caloriesPerDay": "calorias por dia",
    "cal": "cal",
    "dailyTotals": "Totais Diários",
    "calories": "Calorias",
    "modifyPlanTitle": "Precisa modificar seu plano alimentar?",
    "modifyPlanDescription": "Converse com o Assistente IA para personalizar seu plano alimentar ou obter orientação nutricional.",
    "goToAI": "Ir para o Assistente IA",
    "errors": {
      "userDataMissing": "Por favor, preencha suas informações pessoais primeiro!",
      "incompleteData": "Por favor, preencha todas as informações pessoais obrigatórias antes de gerar um plano.",
      "generateFailed": "Falha ao gerar plano alimentar. Por favor, tente novamente mais tarde.",
      "invalidApiKey": "Chave de API inválida. Por favor, verifique sua chave de API do OpenAI.",
      "unavailableModel": "O modelo de IA solicitado está indisponível no momento. Por favor, tente novamente mais tarde.",
      "rateLimitExceeded": "Limite de requisições excedido. Por favor, tente novamente mais tarde.",
      "networkError": "Erro de rede. Por favor, verifique sua conexão com a internet e tente novamente.",
      "invalidUserData": "Dados do usuário inválidos. Por favor, verifique suas informações pessoais e certifique-se de que peso, altura e idade são números válidos.",
      "invalidPlanStructure": "Estrutura de plano alimentar inválida recebida. Por favor, tente gerar o plano novamente."
    }
  },
  "aiAssistant": {
    "title": "Assistente IA",
    "description": "Converse com seu assistente IA para modificar seus planos de treino, planos alimentares, registrar sua alimentação e obter conselhos de fitness personalizados.",
    "placeholder": "Pergunte sobre seus planos de treino ou dieta...",
    "send": "Enviar",
    "loading": "Analisando seus planos de fitness...",
    "plansLoaded": "Planos carregados com sucesso! Vou considerá-los nas minhas respostas.",
    "workoutLoaded": "Plano de treino carregado! Vou referenciá-lo nas minhas respostas.",
    "dietLoaded": "Plano alimentar carregado! Vou referenciá-lo nas minhas respostas.",
    "bothLoaded": "Planos de treino e dieta carregados! Vou considerá-los nas minhas respostas.",
    "clearChat": "Limpar Chat",
    "confirmClearHistory": "Tem certeza que deseja limpar seu histórico de chat?",
    "chatCleared": "Histórico de chat limpo. Como posso ajudá-lo hoje?",
    "completeProfile": "Por favor, complete suas informações de perfil antes de usar o assistente IA.",
    "createPlansFirst": "Gere planos de treino e dieta primeiro para obter conselhos personalizados.",
    "plansLoadedMessage": "Carreguei seu {{workoutPlan}} {{conjunction}} {{dietPlan}} e vou considerá-los nas minhas respostas.",
    "foodLogged": "Alimento '{{foodName}}' registrado com {{calories}} calorias.",
    "workoutPlanChanged": "Plano de treino atualizado com sucesso!",
    "dietPlanChanged": "Plano alimentar atualizado com sucesso!",
    "welcomeMessage": "Olá! Sou seu assistente de fitness com IA. Posso ajudar com dicas de treino, orientação alimentar ou registrar sua alimentação. Como posso ajudá-lo hoje?",
    "errors": {
      "generic": "Desculpe, ocorreu um erro. Por favor, tente novamente.",
      "apiKey": "Erro na chave de API. Por favor, entre em contato com o suporte.",
      "model": "O modelo de IA está indisponível no momento. Por favor, tente novamente mais tarde.",
      "rateLimit": "Você atingiu o número máximo de requisições. Por favor, tente novamente em alguns minutos.",
      "network": "Erro de rede. Por favor, verifique sua conexão com a internet e tente novamente.",
      "completeProfile": "Por favor, complete seu perfil antes de usar o assistente IA."
    }
  },
  "progress": {
    "title": "Acompanhamento de Progresso",
    "addEntry": "Adicionar Registro",
    "date": "Data",
    "weight": "Peso",
    "exercise": "Exercício",
    "sets": "Séries",
    "reps": "Repetições",
    "rest": "Descanso",
    "foodDiary": "Diário Alimentar",
    "addFood": "Registrar Alimento",
    "mealType": "Tipo de Refeição",
    "calories": "Calorias",
    "save": "Salvar",
    "cancel": "Cancelar",
    "noEntries": "Nenhum registro ainda. Comece a acompanhar seu progresso!",
    "successMessage": "Registro adicionado com sucesso!",
    "tabs": {
      "workout": "Registros de Treino",
      "diet": "Registros Alimentares",
      "body": "Medidas Corporais"
    },
    "logYourWorkout": "Registrar Seu Treino",
    "logYourDiet": "Registrar Sua Dieta",
    "foodNamePlaceholder": "ex: Peito de Frango",
    "servingSizePlaceholder": "ex: 100g",
    "createWorkoutPlanFirst": "Por favor, crie um plano de treino primeiro!",
    "goToWorkoutPlans": "Ir para Planos de Treino",
    "fillDataFirst": "Por favor, preencha suas informações pessoais primeiro!",
    "editingExistingLog": "Editando registro existente",
    "protein": "Proteína (g)",
    "carbs": "Carboidratos (g)",
    "fat": "Gordura (g)",
    "servingSize": "Porção",
    "bodyMeasurements": {
      "chest": "Peito",
      "waist": "Cintura",
      "arms": "Braços",
      "hips": "Quadril",
      "thighs": "Coxas",
      "bodyFat": "Gordura Corporal",
      "chestLabel": "Peito (cm, opcional)",
      "waistLabel": "Cintura (cm, opcional)",
      "armsLabel": "Braços (cm, opcional)",
      "hipsLabel": "Quadril (cm, opcional)",
      "thighsLabel": "Coxas (cm, opcional)",
      "bodyFatLabel": "Gordura Corporal (%, opcional)",
      "chestPlaceholder": "Insira medida do peito",
      "waistPlaceholder": "Insira medida da cintura",
      "armsPlaceholder": "Insira medida dos braços",
      "hipPlaceholder": "Insira medida do quadril",
      "thighsPlaceholder": "Insira medida das coxas",
      "bodyFatPlaceholder": "Insira percentual de gordura corporal"
    },
    "notes": "Observações",
    "notesPlaceholder": "Adicione observações sobre suas medidas",
    "set": "Série",
    "weight_kg": "Peso (kg)",
    "workoutHistory": "Histórico de Treinos",
    "history": "Histórico",
    "update": "Atualizar",
    "saveWorkout": "Salvar Treino",
    "updateWorkout": "Atualizar Treino",
    "saveMeasurement": "Salvar Medida",
    "updateMeasurement": "Atualizar Medida",
    "foodItems": "Itens Alimentares",
    "actions": "Ações",
    "remove": "Remover",
    "totals": "Totais",
    "dietLogHistory": "Histórico Alimentar",
    "noFoodsLogged": "Nenhum alimento registrado",
    "macroSummary": "P: {{protein}}g / C: {{carbs}}g / G: {{fat}}g",
    "protein_abbr": "P",
    "carbs_abbr": "C",
    "fat_abbr": "G",
    "food": "Alimento",
    "meal": "Refeição",
    "workout": "Treino",
    "snacks": "Lanches",
    "dietLogSave": "Salvar Registro Alimentar",
    "dietLogUpdate": "Atualizar Registro Alimentar",
    "noFoodEntries": "Nenhum registro alimentar ainda. Comece a registrar sua alimentação acima!",
    "exercises": "exercícios",
    "metrics": {
      "weight": "Peso",
      "bodyFat": "Gordura Corporal",
      "waist": "Cintura",
      "chest": "Peito",
      "arms": "Braços",
      "hips": "Quadril",
      "thighs": "Coxas"
    },
    "charts": {
      "bodyEvolution": "Medidas Corporais ao Longo do Tempo",
      "caloriesTrend": "Calorias Diárias",
      "macrosTrend": "Macros ao Longo do Tempo",
      "volumeEvolution": "Volume de Treino ao Longo do Tempo",
      "setsTrend": "Séries e Exercícios por Treino",
      "totalVolume": "Volume Total (kg)",
      "totalSets": "Total de Séries",
      "exercisesCount": "Exercícios",
      "calories": "Calorias (kcal)",
      "protein": "Proteína",
      "carbs": "Carboidratos",
      "fat": "Gordura",
      "logTodayFood": "Registrar Alimentação de Hoje",
      "logTodayFoodDesc": "Registre suas refeições com detalhes de macros na aba Dieta.",
      "goToDiet": "Ir para Dieta",
      "logWorkout": "Registrar Treino",
      "logWorkoutDesc": "Registre séries, repetições e peso com o registro estilo Hevy.",
      "logWorkoutBtn": "Registrar Treino"
    }
  },
  "fitnessGoals": {
    "build_muscle": "Ganhar Massa Muscular",
    "lose_weight": "Perder Peso",
    "improve_fitness": "Melhorar Condicionamento",
    "increase_strength": "Aumentar Força",
    "maintain_health": "Manter Saúde",
    "muscle_gain": "Ganhar Massa Muscular",
    "weight_loss": "Perder Peso",
    "strength": "Aumentar Força",
    "endurance": "Melhorar Resistência",
    "general_fitness": "Condicionamento Geral"
  },
  "activityLevels": {
    "sedentary": "Sedentário (pouco ou nenhum exercício)",
    "lightly_active": "Levemente Ativo (exercício leve 1-3 dias/semana)",
    "moderately_active": "Moderadamente Ativo (exercício moderado 3-5 dias/semana)",
    "very_active": "Muito Ativo (exercício intenso 6-7 dias/semana)",
    "extremely_active": "Extremamente Ativo (exercício muito intenso e trabalho físico)"
  },
  "mealTypes": {
    "breakfast": "Café da Manhã",
    "lunch": "Almoço",
    "dinner": "Jantar",
    "snack": "Lanche"
  },
  "common": {
    "loading": "Carregando...",
    "errorOccurred": "Ocorreu um erro",
    "tryAgain": "Por favor, tente novamente",
    "noData": "Nenhum dado disponível",
    "save": "Salvar",
    "cancel": "Cancelar",
    "delete": "Excluir",
    "edit": "Editar",
    "add": "Adicionar",
    "remove": "Remover",
    "confirm": "Confirmar",
    "back": "Voltar",
    "next": "Próximo",
    "done": "Concluído",
    "success": "Sucesso!",
    "error": "Erro!",
    "yes": "Sim",
    "no": "Não",
    "male": "Masculino",
    "female": "Feminino",
    "other": "Outro",
    "settings": "Configurações",
    "language": "Idioma",
    "step": "Etapa",
    "of": "de",
    "complete": "Completo",
    "greeting": "Olá",
    "goToUserData": "Ir para Dados do Usuário",
    "workoutPlan": "Plano de Treino",
    "dietPlan": "Plano Alimentar",
    "and": "e",
    "successfully": "com sucesso",
    "updated": "atualizado",
    "renderError": "Houve um erro ao exibir o plano. Por favor, reinicie e tente novamente.",
    "resetPlan": "Reiniciar Plano",
    "errorLoadingData": "Erro ao carregar dados. Por favor, atualize a página.",
    "untitled": "Plano Sem Título",
    "noDescription": "Nenhuma descrição disponível",
    "day": "Dia",
    "meal": "Refeição",
    "appName": "Muscle Guide",
    "synced": "Sincronizado com servidor",
    "savedLocally": "Salvo localmente",
    "syncing": "Sincronizando...",
    "syncError": "Erro de sincronização",
    "sync": "Sincronizar com servidor",
    "submitting": "Enviando..."
  },
  "errors": {
    "nameRequired": "Nome é obrigatório",
    "invalidEmail": "Por favor, insira um endereço de e-mail válido",
    "passwordLength": "A senha deve ter pelo menos 8 caracteres",
    "passwordWeak": "A senha é muito fraca. Adicione mais complexidade",
    "passwordsNoMatch": "As senhas não coincidem",
    "ageRequired": "Idade é obrigatória",
    "ageRange": "A idade deve estar entre 16 e 100",
    "weightRequired": "Peso é obrigatório",
    "weightRange": "O peso deve estar entre 30 e 300 kg",
    "heightRequired": "Altura é obrigatória",
    "heightRange": "A altura deve estar entre 100 e 250 cm"
  },
  "languageSelection": {
    "language": "Idioma",
    "en": "Inglês",
    "pt-BR": "Português Brasileiro",
    "es": "Espanhol"
  },
  "notifications": {
    "success": "Sucesso!",
    "error": "Erro!",
    "warning": "Atenção!",
    "info": "Informação"
  },
  "workoutDays": {
    "upperBody": "Parte Superior",
    "lowerBody": "Parte Inferior",
    "fullBody": "Corpo Inteiro",
    "push": "Dia de Empurrar",
    "pull": "Dia de Puxar",
    "legs": "Dia de Pernas",
    "chest": "Dia de Peito",
    "back": "Dia de Costas",
    "shoulders": "Dia de Ombros",
    "arms": "Dia de Braços",
    "core": "Dia de Core",
    "cardio": "Dia de Cardio",
    "rest": "Dia de Descanso",
    "day1": "Dia 1",
    "day2": "Dia 2",
    "day3": "Dia 3",
    "day4": "Dia 4",
    "day5": "Dia 5",
    "day6": "Dia 6",
    "day7": "Dia 7"
  },
  "bodyParts": {
    "chest": "Peito",
    "back": "Costas",
    "shoulders": "Ombros",
    "biceps": "Bíceps",
    "triceps": "Tríceps",
    "legs": "Pernas",
    "quads": "Quadríceps",
    "hamstrings": "Posteriores",
    "calves": "Panturrilhas",
    "glutes": "Glúteos",
    "abs": "Abdominais",
    "core": "Core",
    "fullBody": "Corpo Inteiro",
    "upperBody": "Parte Superior",
    "lowerBody": "Parte Inferior"
  },
  "workoutLocations": {
    "home": "Casa",
    "gym": "Academia",
    "outdoor": "Ao Ar Livre"
  },
  "experienceLevels": {
    "beginner": "Iniciante",
    "intermediate": "Intermediário",
    "advanced": "Avançado"
  },
  "subscription": {
    "headline": "Desbloqueie planos personalizados de treino e dieta com IA",
    "monthlyPlan": "Plano Mensal",
    "month": "mês",
    "subscribeBtn": "Assinar — $10/mês",
    "redirecting": "Redirecionando para pagamento...",
    "checkoutError": "Falha ao criar sessão de pagamento. Tente novamente.",
    "portalError": "Falha ao abrir portal de assinatura.",
    "active": "Assinatura Ativa!",
    "plan": "Plano",
    "goToDashboard": "Ir para o Painel",
    "manage": "Gerenciar Assinatura",
    "activating": "Ativando sua assinatura...",
    "cancelled": "Pagamento cancelado. Você pode tentar novamente quando quiser.",
    "features": {
      "aiWorkoutPlans": "Planos de Treino com IA",
      "aiDietPlans": "Planos Alimentares com IA",
      "aiChatAssistant": "Assistente IA por Chat",
      "progressTracking": "Acompanhamento de Progresso",
      "evolutionCharts": "Gráficos de Evolução e Análises"
    }
  }
};

// Spanish translations
const translationES = {
  "app": {
    "title": "Muscle Guide - Tu Asistente de Fitness con IA",
    "meta": {
      "description": "Un asistente de fitness personalizado con IA para planes de entrenamiento, planes de dieta y seguimiento de fitness."
    }
  },
  "register": {
    "title": "Crea Tu Cuenta",
    "subtitle": "Únete a Muscle Guide para seguir tu viaje de fitness",
    "step1": "Información de la Cuenta",
    "step2": "Información Personal",
    "step3": "Información de Fitness",
    "alreadyHaveAccount": "¿Ya tienes una cuenta?",
    "createAccount": "Crear Cuenta",
    "submitting": "Creando cuenta..."
  },
  "login": {
    "title": "Iniciar Sesión"
  },
  "auth": {
    "login": "Iniciar Sesión",
    "register": "Registrarse",
    "logout": "Cerrar Sesión",
    "email": "Correo Electrónico",
    "password": "Contraseña",
    "confirmPassword": "Confirmar Contraseña",
    "forgotPassword": "¿Olvidaste tu contraseña?",
    "dontHaveAccount": "¿No tienes una cuenta?",
    "alreadyHaveAccount": "¿Ya tienes una cuenta?",
    "signUp": "Registrarse",
    "signIn": "Iniciar Sesión",
    "errorMessage": "Correo electrónico o contraseña inválidos",
    "successRegister": "¡Registro exitoso! Ahora puedes iniciar sesión.",
    "passwordWeak": "Débil",
    "passwordMedium": "Media",
    "passwordStrong": "Fuerte",
    "resetPasswordTitle": "Restablecer tu contraseña",
    "forgotPasswordDesc": "Ingresa el correo asociado a tu cuenta y te enviaremos un enlace para restablecer tu contraseña.",
    "emailPlaceholder": "tu@email.com",
    "sendResetLink": "Enviar Enlace de Restablecimiento",
    "backToLogin": "Volver al Inicio de Sesión",
    "resetEmailSent": "Revisa tu correo",
    "resetEmailSentDesc": "Te enviamos un enlace para restablecer tu contraseña. El enlace expira en 1 hora.",
    "resetError": "Algo salió mal. Inténtalo de nuevo.",
    "resetPassword": "Restablecer Contraseña",
    "setNewPassword": "Establece una nueva contraseña para tu cuenta",
    "newPassword": "Nueva Contraseña",
    "passwordMinLength": "La contraseña debe tener al menos 8 caracteres",
    "passwordMismatch": "Las contraseñas no coinciden",
    "passwordResetSuccess": "¡Contraseña restablecida con éxito!",
    "passwordResetSuccessDesc": "Ahora puedes iniciar sesión con tu nueva contraseña.",
    "invalidResetLink": "Enlace Inválido",
    "invalidResetLinkDesc": "Este enlace de restablecimiento es inválido o ha expirado.",
    "requestNewLink": "Solicitar nuevo enlace"
  },
  "dashboard": {
    "welcome": "Bienvenido",
    "subtitle": "¿Qué te gustaría hacer hoy?",
    "workoutPlans": "Planes de Entrenamiento",
    "workoutPlansDesc": "Genera y gestiona tus rutinas de entrenamiento con IA",
    "dietPlans": "Planes de Dieta",
    "dietPlansDesc": "Crea planes de comidas personalizados y controla la nutrición diaria",
    "progress": "Seguimiento de Progreso",
    "progressDesc": "Registra medidas corporales, entrenamientos y ve gráficos de evolución",
    "aiAssistant": "Asistente IA",
    "aiAssistantDesc": "Chatea con la IA para personalizar planes y registrar comidas",
    "profile": "Perfil y Configuración",
    "profileDesc": "Actualiza tu información personal, metas y preferencias"
  },
  "navigation": {
    "profile": "Perfil",
    "workoutPlans": "Planes de Entrenamiento",
    "dietPlans": "Planes de Dieta",
    "progress": "Progreso",
    "logout": "Cerrar Sesión",
    "home": "Inicio",
    "settings": "Configuración",
    "chatWithAI": "Chatear con IA"
  },
  "registration": {
    "step1": "Información de la Cuenta",
    "step2": "Información Personal",
    "step3": "Información de Fitness",
    "next": "Siguiente",
    "previous": "Anterior",
    "submit": "Crear Cuenta",
    "completeFitnessProfile": "Completa tu perfil de fitness para obtener planes de entrenamiento y dieta personalizados",
    "selectFitnessObjective": "Selecciona tu objetivo principal de fitness",
    "importantForDiet": "Importante para el Plan de Dieta",
    "affectsCalorieRequirements": "Esto afecta tus requerimientos calóricos y recomendaciones dietéticas",
    "howLongExercise": "¿Cuánto tiempo puedes ejercitarte en una sola sesión?",
    "howManyDays": "¿Cuántos días puedes comprometerte a entrenar cada semana?",
    "whereWorkout": "¿Dónde entrenarás?",
    "bodyweight": "Ejercicios con Peso Corporal",
    "affectsWorkoutPlans": "Afecta los Planes de Entrenamiento",
    "currentExperience": "Tu experiencia actual de fitness/entrenamiento",
    "day": "día",
    "days": "días",
    "year": "año de entrenamiento",
    "years": "años de entrenamiento"
  },
  "nav": {
    "userData": "Datos del Usuario",
    "workoutPlans": "Planes de Entrenamiento",
    "dietPlans": "Planes de Dieta",
    "aiAssistant": "Asistente IA",
    "progress": "Progreso"
  },
  "userData": {
    "title": "Tu Perfil",
    "personalInfo": "Información Personal",
    "name": "Nombre",
    "age": "Edad",
    "gender": "Género",
    "weight": "Peso",
    "height": "Altura",
    "fitnessGoals": "Objetivos de Fitness",
    "workoutSettings": "Configuración de Entrenamiento",
    "timePerSession": "Tiempo por Sesión",
    "daysPerWeek": "Días por Semana",
    "activityLevel": "Nivel de Actividad",
    "workoutLocation": "Lugar de Entrenamiento",
    "experienceLevel": "Nivel de Experiencia",
    "save": "Guardar Cambios",
    "saved": "¡Cambios guardados con éxito!",
    "confirmMessage": "Por favor, confirma tu información",
    "minutesAbbr": "min",
    "cm": "cm",
    "kg": "kg",
    "loadSuccess": "¡Datos del usuario cargados con éxito!",
    "saveSuccess": "¡Información guardada con éxito!",
    "saveError": "Error al actualizar el perfil. Por favor, inténtalo de nuevo."
  },
  "workoutPlans": {
    "title": "Planes de Entrenamiento",
    "createNew": "Crear Nuevo Plan",
    "beginner": "Principiante",
    "intermediate": "Intermedio",
    "advanced": "Avanzado",
    "modifyPlan": "¿Necesitas modificar tu plan de entrenamiento?",
    "chatAIAssistant": "Chatea con el Asistente IA para personalizar tu plan de entrenamiento u obtener orientación sobre ejercicios específicos.",
    "goAIAssistant": "Ir al Asistente IA",
    "generateNew": "Generar Nuevo Plan",
    "exerciseInfoTip": "Haz clic en cualquier nombre de ejercicio para ver cómo realizarlo en YouTube.",
    "yourWorkoutPlan": "Tu Plan de Entrenamiento",
    "askAboutPlan": "Permíteme crear un plan de entrenamiento personalizado basado en tus objetivos.",
    "details": "Detalles",
    "goal": "Objetivo",
    "level": "Nivel",
    "duration": "Duración",
    "weeks": "semanas",
    "location": "Lugar",
    "overview": "Resumen",
    "schedule": "Horario",
    "additionalNotes": "Notas Adicionales",
    "progressionPlan": "Plan de Progresión",
    "videoGuide": "Guía en Video",
    "findVideo": "Encontrar Guía en Video",
    "generate": "Generar Plan de Entrenamiento",
    "loading": "Cargando planes...",
    "noPlans": "No se encontraron planes de entrenamiento. ¡Genera uno para empezar!",
    "missingUserData": "Para generar un plan de entrenamiento personalizado, necesito alguna información sobre ti:",
    "fillUserData": "Completar Datos",
    "savedPlans": "Planes Guardados",
    "planTitle": "Plan {{number}}",
    "createPlan": "Crear un Plan de Entrenamiento",
    "manageUserData": "Ingresa Tu Información",
    "generating": "Generando tu plan de entrenamiento...",
    "saveToDevice": "Guardar en dispositivo",
    "saveToAccount": "Guardar en cuenta",
    "generateButton": "Generar Plan de Entrenamiento",
    "generated": "Plan de Entrenamiento Generado",
    "previous": "Anterior",
    "save": "Guardar",
    "next": "Siguiente",
    "exercise": "Ejercicio",
    "sets": "Series",
    "reps": "Repeticiones",
    "rest": "Descanso",
    "loadMore": "Cargar Más"
  },
  "dietPlans": {
    "title": "Planes de Dieta",
    "createNew": "Crear Nuevo Plan",
    "generating": "Generando tu plan de dieta personalizado...",
    "dailyCalories": "Calorías Diarias",
    "macros": "Macronutrientes",
    "protein": "Proteína",
    "carbs": "Carbohidratos",
    "fat": "Grasa",
    "breakfast": "Desayuno",
    "lunch": "Almuerzo",
    "dinner": "Cena",
    "snack": "Merienda",
    "ingredients": "Ingredientes",
    "instructions": "Instrucciones",
    "modify": "Modificar Plan",
    "delete": "Eliminar Plan",
    "fillDataFirst": "¡Por favor, completa tu información personal primero!",
    "readyToCreate": "¿Listo para crear tu plan de dieta personalizado?",
    "generate": "Generar Plan de Dieta",
    "generateNew": "Generar Nuevo Plan",
    "caloriesPerDay": "calorías por día",
    "cal": "cal",
    "dailyTotals": "Totales Diarios",
    "calories": "Calorías",
    "modifyPlanTitle": "¿Necesitas modificar tu plan de dieta?",
    "modifyPlanDescription": "Chatea con el Asistente IA para personalizar tu plan de dieta u obtener orientación nutricional.",
    "goToAI": "Ir al Asistente IA",
    "errors": {
      "userDataMissing": "¡Por favor, completa tu información personal primero!",
      "incompleteData": "Por favor, completa toda la información personal requerida antes de generar un plan.",
      "generateFailed": "Error al generar el plan de dieta. Por favor, inténtalo de nuevo más tarde.",
      "invalidApiKey": "Clave de API inválida. Por favor, verifica tu clave de API de OpenAI.",
      "unavailableModel": "El modelo de IA solicitado no está disponible en este momento. Por favor, inténtalo de nuevo más tarde.",
      "rateLimitExceeded": "Límite de solicitudes excedido. Por favor, inténtalo de nuevo más tarde.",
      "networkError": "Error de red. Por favor, verifica tu conexión a internet e inténtalo de nuevo.",
      "invalidUserData": "Datos de usuario inválidos. Por favor, verifica tu información personal y asegúrate de que peso, altura y edad sean números válidos.",
      "invalidPlanStructure": "Estructura de plan de dieta inválida recibida. Por favor, intenta generar el plan de nuevo."
    }
  },
  "aiAssistant": {
    "title": "Asistente IA",
    "description": "Chatea con tu asistente IA para modificar tus planes de entrenamiento, planes de dieta, registrar tu alimentación y obtener consejos de fitness personalizados.",
    "placeholder": "Pregunta sobre tus planes de entrenamiento o dieta...",
    "send": "Enviar",
    "loading": "Analizando tus planes de fitness...",
    "plansLoaded": "¡Planes cargados con éxito! Los consideraré en mis respuestas.",
    "workoutLoaded": "¡Plan de entrenamiento cargado! Lo referenciaré en mis respuestas.",
    "dietLoaded": "¡Plan de dieta cargado! Lo referenciaré en mis respuestas.",
    "bothLoaded": "¡Planes de entrenamiento y dieta cargados! Los consideraré en mis respuestas.",
    "clearChat": "Limpiar Chat",
    "confirmClearHistory": "¿Estás seguro de que quieres borrar tu historial de chat?",
    "chatCleared": "Historial de chat borrado. ¿Cómo puedo ayudarte hoy?",
    "completeProfile": "Por favor, completa la información de tu perfil antes de usar el asistente IA.",
    "createPlansFirst": "Genera planes de entrenamiento y dieta primero para obtener consejos personalizados.",
    "plansLoadedMessage": "He cargado tu {{workoutPlan}} {{conjunction}} {{dietPlan}} y los consideraré en mis respuestas.",
    "foodLogged": "Alimento '{{foodName}}' registrado con {{calories}} calorías.",
    "workoutPlanChanged": "¡Plan de entrenamiento actualizado con éxito!",
    "dietPlanChanged": "¡Plan de dieta actualizado con éxito!",
    "welcomeMessage": "¡Hola! Soy tu asistente de fitness con IA. Puedo ayudarte con consejos de entrenamiento, orientación dietética o registrar tu alimentación. ¿Cómo puedo ayudarte hoy?",
    "errors": {
      "generic": "Lo siento, ocurrió un error. Por favor, inténtalo de nuevo.",
      "apiKey": "Error de clave de API. Por favor, contacta al soporte.",
      "model": "El modelo de IA no está disponible en este momento. Por favor, inténtalo de nuevo más tarde.",
      "rateLimit": "Has alcanzado el número máximo de solicitudes. Por favor, inténtalo de nuevo en unos minutos.",
      "network": "Error de red. Por favor, verifica tu conexión a internet e inténtalo de nuevo.",
      "completeProfile": "Por favor, completa tu perfil antes de usar el asistente IA."
    }
  },
  "progress": {
    "title": "Seguimiento de Progreso",
    "addEntry": "Añadir Registro",
    "date": "Fecha",
    "weight": "Peso",
    "exercise": "Ejercicio",
    "sets": "Series",
    "reps": "Repeticiones",
    "rest": "Descanso",
    "foodDiary": "Diario Alimenticio",
    "addFood": "Registrar Alimento",
    "mealType": "Tipo de Comida",
    "calories": "Calorías",
    "save": "Guardar",
    "cancel": "Cancelar",
    "noEntries": "No hay registros aún. ¡Comienza a seguir tu progreso!",
    "successMessage": "¡Registro añadido con éxito!",
    "tabs": {
      "workout": "Registros de Entrenamiento",
      "diet": "Registros Alimenticios",
      "body": "Medidas Corporales"
    },
    "logYourWorkout": "Registrar Tu Entrenamiento",
    "logYourDiet": "Registrar Tu Dieta",
    "foodNamePlaceholder": "ej: Pechuga de Pollo",
    "servingSizePlaceholder": "ej: 100g",
    "createWorkoutPlanFirst": "¡Por favor, crea un plan de entrenamiento primero!",
    "goToWorkoutPlans": "Ir a Planes de Entrenamiento",
    "fillDataFirst": "¡Por favor, completa tu información personal primero!",
    "editingExistingLog": "Editando registro existente",
    "protein": "Proteína (g)",
    "carbs": "Carbohidratos (g)",
    "fat": "Grasa (g)",
    "servingSize": "Porción",
    "bodyMeasurements": {
      "chest": "Pecho",
      "waist": "Cintura",
      "arms": "Brazos",
      "hips": "Caderas",
      "thighs": "Muslos",
      "bodyFat": "Grasa Corporal",
      "chestLabel": "Pecho (cm, opcional)",
      "waistLabel": "Cintura (cm, opcional)",
      "armsLabel": "Brazos (cm, opcional)",
      "hipsLabel": "Caderas (cm, opcional)",
      "thighsLabel": "Muslos (cm, opcional)",
      "bodyFatLabel": "Grasa Corporal (%, opcional)",
      "chestPlaceholder": "Ingresa medida del pecho",
      "waistPlaceholder": "Ingresa medida de la cintura",
      "armsPlaceholder": "Ingresa medida de los brazos",
      "hipPlaceholder": "Ingresa medida de las caderas",
      "thighsPlaceholder": "Ingresa medida de los muslos",
      "bodyFatPlaceholder": "Ingresa porcentaje de grasa corporal"
    },
    "notes": "Notas",
    "notesPlaceholder": "Añade notas sobre tus medidas",
    "set": "Serie",
    "weight_kg": "Peso (kg)",
    "workoutHistory": "Historial de Entrenamientos",
    "history": "Historial",
    "update": "Actualizar",
    "saveWorkout": "Guardar Entrenamiento",
    "updateWorkout": "Actualizar Entrenamiento",
    "saveMeasurement": "Guardar Medida",
    "updateMeasurement": "Actualizar Medida",
    "foodItems": "Alimentos",
    "actions": "Acciones",
    "remove": "Eliminar",
    "totals": "Totales",
    "dietLogHistory": "Historial Alimenticio",
    "noFoodsLogged": "No hay alimentos registrados",
    "macroSummary": "P: {{protein}}g / C: {{carbs}}g / G: {{fat}}g",
    "protein_abbr": "P",
    "carbs_abbr": "C",
    "fat_abbr": "G",
    "food": "Alimento",
    "meal": "Comida",
    "workout": "Entrenamiento",
    "snacks": "Meriendas",
    "dietLogSave": "Guardar Registro Alimenticio",
    "dietLogUpdate": "Actualizar Registro Alimenticio",
    "noFoodEntries": "No hay registros alimenticios aún. ¡Comienza a registrar tu alimentación arriba!",
    "exercises": "ejercicios",
    "metrics": {
      "weight": "Peso",
      "bodyFat": "Grasa Corporal",
      "waist": "Cintura",
      "chest": "Pecho",
      "arms": "Brazos",
      "hips": "Caderas",
      "thighs": "Muslos"
    },
    "charts": {
      "bodyEvolution": "Medidas Corporales a lo Largo del Tiempo",
      "caloriesTrend": "Calorías Diarias",
      "macrosTrend": "Macros a lo Largo del Tiempo",
      "volumeEvolution": "Volumen de Entrenamiento a lo Largo del Tiempo",
      "setsTrend": "Series y Ejercicios por Entrenamiento",
      "totalVolume": "Volumen Total (kg)",
      "totalSets": "Total de Series",
      "exercisesCount": "Ejercicios",
      "calories": "Calorías (kcal)",
      "protein": "Proteína",
      "carbs": "Carbohidratos",
      "fat": "Grasa",
      "logTodayFood": "Registrar Comida de Hoy",
      "logTodayFoodDesc": "Registra tus comidas con detalles de macros en la pestaña Dieta.",
      "goToDiet": "Ir a Dieta",
      "logWorkout": "Registrar Entrenamiento",
      "logWorkoutDesc": "Registra series, repeticiones y peso con el registro estilo Hevy.",
      "logWorkoutBtn": "Registrar Entrenamiento"
    }
  },
  "fitnessGoals": {
    "build_muscle": "Ganar Masa Muscular",
    "lose_weight": "Perder Peso",
    "improve_fitness": "Mejorar Condición Física",
    "increase_strength": "Aumentar Fuerza",
    "maintain_health": "Mantener Salud",
    "muscle_gain": "Ganar Masa Muscular",
    "weight_loss": "Perder Peso",
    "strength": "Aumentar Fuerza",
    "endurance": "Mejorar Resistencia",
    "general_fitness": "Condición Física General"
  },
  "activityLevels": {
    "sedentary": "Sedentario (poco o nada de ejercicio)",
    "lightly_active": "Ligeramente Activo (ejercicio ligero 1-3 días/semana)",
    "moderately_active": "Moderadamente Activo (ejercicio moderado 3-5 días/semana)",
    "very_active": "Muy Activo (ejercicio intenso 6-7 días/semana)",
    "extremely_active": "Extremadamente Activo (ejercicio muy intenso y trabajo físico)"
  },
  "mealTypes": {
    "breakfast": "Desayuno",
    "lunch": "Almuerzo",
    "dinner": "Cena",
    "snack": "Merienda"
  },
  "common": {
    "loading": "Cargando...",
    "errorOccurred": "Ha ocurrido un error",
    "tryAgain": "Por favor, inténtalo de nuevo",
    "noData": "No hay datos disponibles",
    "save": "Guardar",
    "cancel": "Cancelar",
    "delete": "Eliminar",
    "edit": "Editar",
    "add": "Añadir",
    "remove": "Eliminar",
    "confirm": "Confirmar",
    "back": "Atrás",
    "next": "Siguiente",
    "done": "Hecho",
    "success": "¡Éxito!",
    "error": "¡Error!",
    "yes": "Sí",
    "no": "No",
    "male": "Masculino",
    "female": "Femenino",
    "other": "Otro",
    "settings": "Configuración",
    "language": "Idioma",
    "step": "Paso",
    "of": "de",
    "complete": "Completo",
    "greeting": "Hola",
    "goToUserData": "Ir a Datos de Usuario",
    "workoutPlan": "Plan de Entrenamiento",
    "dietPlan": "Plan de Dieta",
    "and": "y",
    "successfully": "con éxito",
    "updated": "actualizado",
    "renderError": "Hubo un error al mostrar el plan. Por favor, reinicia e inténtalo de nuevo.",
    "resetPlan": "Reiniciar Plan",
    "errorLoadingData": "Error al cargar datos. Por favor, actualiza la página.",
    "untitled": "Plan Sin Título",
    "noDescription": "Sin descripción disponible",
    "day": "Día",
    "meal": "Comida",
    "appName": "Muscle Guide",
    "synced": "Sincronizado con servidor",
    "savedLocally": "Guardado localmente",
    "syncing": "Sincronizando...",
    "syncError": "Error de sincronización",
    "sync": "Sincronizar con servidor",
    "submitting": "Enviando..."
  },
  "errors": {
    "nameRequired": "El nombre es obligatorio",
    "invalidEmail": "Por favor, introduce una dirección de correo electrónico válida",
    "passwordLength": "La contraseña debe tener al menos 8 caracteres",
    "passwordWeak": "La contraseña es demasiado débil. Añade más complejidad",
    "passwordsNoMatch": "Las contraseñas no coinciden",
    "ageRequired": "La edad es obligatoria",
    "ageRange": "La edad debe estar entre 16 y 100",
    "weightRequired": "El peso es obligatorio",
    "weightRange": "El peso debe estar entre 30 y 300 kg",
    "heightRequired": "La altura es obligatoria",
    "heightRange": "La altura debe estar entre 100 y 250 cm"
  },
  "languageSelection": {
    "language": "Idioma",
    "en": "Inglés",
    "pt-BR": "Portugués Brasileño",
    "es": "Español"
  },
  "notifications": {
    "success": "¡Éxito!",
    "error": "¡Error!",
    "warning": "¡Advertencia!",
    "info": "Información"
  },
  "workoutDays": {
    "upperBody": "Tren Superior",
    "lowerBody": "Tren Inferior",
    "fullBody": "Cuerpo Completo",
    "push": "Día de Empuje",
    "pull": "Día de Tirón",
    "legs": "Día de Piernas",
    "chest": "Día de Pecho",
    "back": "Día de Espalda",
    "shoulders": "Día de Hombros",
    "arms": "Día de Brazos",
    "core": "Día de Core",
    "cardio": "Día de Cardio",
    "rest": "Día de Descanso",
    "day1": "Día 1",
    "day2": "Día 2",
    "day3": "Día 3",
    "day4": "Día 4",
    "day5": "Día 5",
    "day6": "Día 6",
    "day7": "Día 7"
  },
  "bodyParts": {
    "chest": "Pecho",
    "back": "Espalda",
    "shoulders": "Hombros",
    "biceps": "Bíceps",
    "triceps": "Tríceps",
    "legs": "Piernas",
    "quads": "Cuádriceps",
    "hamstrings": "Isquiotibiales",
    "calves": "Pantorrillas",
    "glutes": "Glúteos",
    "abs": "Abdominales",
    "core": "Core",
    "fullBody": "Cuerpo Completo",
    "upperBody": "Tren Superior",
    "lowerBody": "Tren Inferior"
  },
  "workoutLocations": {
    "home": "Casa",
    "gym": "Gimnasio",
    "outdoor": "Al Aire Libre"
  },
  "experienceLevels": {
    "beginner": "Principiante",
    "intermediate": "Intermedio",
    "advanced": "Avanzado"
  },
  "subscription": {
    "headline": "Desbloquea planes personalizados de entrenamiento y dieta con IA",
    "monthlyPlan": "Plan Mensual",
    "month": "mes",
    "subscribeBtn": "Suscribirse — $10/mes",
    "redirecting": "Redirigiendo al pago...",
    "checkoutError": "Error al crear sesión de pago. Inténtalo de nuevo.",
    "portalError": "Error al abrir el portal de suscripción.",
    "active": "¡Suscripción Activa!",
    "plan": "Plan",
    "goToDashboard": "Ir al Panel",
    "manage": "Gestionar Suscripción",
    "activating": "Activando tu suscripción...",
    "cancelled": "Pago cancelado. Puedes intentarlo de nuevo cuando quieras.",
    "features": {
      "aiWorkoutPlans": "Planes de Entrenamiento con IA",
      "aiDietPlans": "Planes de Dieta con IA",
      "aiChatAssistant": "Asistente IA por Chat",
      "progressTracking": "Seguimiento de Progreso",
      "evolutionCharts": "Gráficos de Evolución y Análisis"
    }
  }
};

// the translations
const resources = {
  en: {
    translation: translationEN
  },
  'pt-BR': {
    translation: translationPTBR
  },
  es: {
    translation: translationES
  }
};

i18n
  // Comment out Backend if we're using embedded resources
  // .use(Backend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'en',
    debug: process.env.NODE_ENV === 'development',
    saveMissing: true,
    interpolation: {
      escapeValue: false, // not needed for react as it escapes by default
    },
    react: {
      useSuspense: true, // Set to true to enable Suspense
    },
    detection: {
      order: ['localStorage', 'cookie', 'navigator'],
      caches: ['localStorage', 'cookie'],
    }
  });

export default i18n; 